import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Search, X } from 'lucide-react';
import ReminderCard from '../components/dashboard/ReminderCard';
import {
  reminderApi,
  Reminder,
  ReminderStatus,
  ReminderType,
  ReminderPriority,
  EntityType,
  getReminderTypeLabel,
  handleApiError
} from '../services/reminderService';
import { leadsApi } from '../services/leadsService';
import { clientsApi } from '../services/clientsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 10px;
  display: inline-flex;
  align-items: center;
  color: var(--text-tertiary);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 240px;
  padding: 8px 32px 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: var(--surface-color);
  color: var(--text-primary);
  cursor: pointer;
  min-width: 130px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const PickerWrapper = styled.div`
  position: relative;
  width: 220px;
`;

const PickerInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const PickerDropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  z-index: 20;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PickerOption = styled.li`
  padding: 8px 12px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
  }
`;

const PickerEmpty = styled.li`
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text-tertiary);
`;

const SelectedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--hover-background);
  white-space: nowrap;
`;

const ChipClear = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--error-color);
  }
`;

const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const StateBox = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorBox = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--error-color);
  font-size: 15px;
`;

const PaginationContainer = styled.div`
  padding: 16px 4px;
  margin-top: 16px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: ${props => props.$active ? 'var(--primary-color)' : 'var(--surface-color)'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? 'var(--primary-hover)' : 'var(--hover-background)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PAGE_SIZE = 50;

interface EntityOption {
  id: number;
  name: string;
}

// בורר ליד/לקוח עם חיפוש שרת (debounce) — אין רכיב Autocomplete משותף בפרויקט
const EntityPicker: React.FC<{
  kind: 'lead' | 'client';
  onSelect: (option: EntityOption) => void;
}> = ({ kind, onSelect }) => {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!term.trim()) {
      setOptions([]);
      return;
    }

    const handler = setTimeout(async () => {
      setLoading(true);
      try {
        if (kind === 'lead') {
          const result = await leadsApi.search(term.trim(), { page: 1, pageSize: 10 });
          setOptions(result.leads.map(l => ({ id: l.id, name: l.fullName })));
        } else {
          const result = await clientsApi.getAll({ searchTerm: term.trim(), page: 1, pageSize: 10 });
          setOptions(result.clients.map(c => ({ id: c.id, name: c.fullName })));
        }
        setOpen(true);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [term, kind]);

  return (
    <PickerWrapper>
      <PickerInput
        type="text"
        placeholder={kind === 'lead' ? 'חיפוש ליד...' : 'חיפוש לקוח...'}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => options.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && term.trim() && (
        <PickerDropdown>
          {loading ? (
            <PickerEmpty>מחפש...</PickerEmpty>
          ) : options.length === 0 ? (
            <PickerEmpty>לא נמצאו תוצאות</PickerEmpty>
          ) : (
            options.map(opt => (
              <PickerOption
                key={opt.id}
                onMouseDown={() => {
                  onSelect(opt);
                  setTerm('');
                  setOpen(false);
                }}
              >
                {opt.name}
              </PickerOption>
            ))
          )}
        </PickerDropdown>
      )}
    </PickerWrapper>
  );
};

const RemindersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // קריאת מצב הפילטרים מה-URL
  // ברירת מחדל לסטטוס: "ממתין" כשהפרמטר חסר; הערך 'all' = ללא סינון סטטוס
  const statusParam = searchParams.get('status') ?? String(ReminderStatus.Pending);
  const typeParam = searchParams.get('type') ?? '';
  const priorityParam = searchParams.get('priority') ?? '';
  const fromParam = searchParams.get('from') ?? '';
  const toParam = searchParams.get('to') ?? '';
  const searchParam = searchParams.get('search') ?? '';
  const entityTypeParam = searchParams.get('entityType') ?? '';
  const entityIdParam = searchParams.get('entityId') ?? '';
  const entityNameParam = searchParams.get('entityName') ?? '';
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10) || 1;

  const [searchInput, setSearchInput] = useState(searchParam);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // עדכון פרמטר ב-URL; כל שינוי פילטר מאפס לעמוד 1
  const updateParam = useCallback((key: string, value: string, resetPage = true) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      if (resetPage && key !== 'page') {
        next.delete('page');
      }
      return next;
    });
  }, [setSearchParams]);

  // שינוי סוג הישות המקושרת — מנקה את הישות הספציפית שנבחרה
  const handleEntityTypeChange = useCallback((value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('entityType', value);
      } else {
        next.delete('entityType');
      }
      next.delete('entityId');
      next.delete('entityName');
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  // בחירת ישות ספציפית מהבורר (שומר גם שם להצגה ב-URL)
  const handleEntitySelect = useCallback((option: { id: number; name: string }) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('entityId', String(option.id));
      next.set('entityName', option.name);
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  const handleEntityClear = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('entityId');
      next.delete('entityName');
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  // debounce לחיפוש — שולח לשרת רק אחרי שהמשתמש הפסיק להקליד
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== searchParam) {
        updateParam('search', searchInput);
      }
    }, 350);
    return () => clearTimeout(handler);
  }, [searchInput, searchParam, updateParam]);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    setError(null);

    // ביטול בקשה קודמת (מונע race condition בחיפוש)
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await reminderApi.getReminders({
        page: pageParam,
        pageSize: PAGE_SIZE,
        searchTerm: searchParam || undefined,
        // 'all' = ללא סינון סטטוס; אחרת הסטטוס שנבחר
        status: statusParam && statusParam !== 'all' ? parseInt(statusParam, 10) : undefined,
        reminderType: typeParam ? parseInt(typeParam, 10) : undefined,
        priority: priorityParam ? parseInt(priorityParam, 10) : undefined,
        entityType: entityTypeParam ? parseInt(entityTypeParam, 10) : undefined,
        entityId: entityIdParam ? parseInt(entityIdParam, 10) : undefined,
        fromDate: fromParam || undefined,
        toDate: toParam || undefined
      }, controller.signal);

      setReminders(result.reminders);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return; // בקשה בוטלה — אין צורך בטיפול
      }
      setError(handleApiError(err));
      setReminders([]);
      setTotalCount(0);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [pageParam, searchParam, statusParam, typeParam, priorityParam, entityTypeParam, entityIdParam, fromParam, toParam]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const goToPage = (page: number) => {
    updateParam('page', String(page), false);
    window.scrollTo(0, 0);
  };

  const renderPagination = () => {
    if (totalCount === 0) return null;

    const startItem = (pageParam - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(pageParam * PAGE_SIZE, totalCount);

    const maxVisible = 5;
    let startPage = Math.max(1, pageParam - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    startPage = Math.max(1, endPage - maxVisible + 1);

    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <PaginationContainer>
        <PaginationInfo>
          מציג {startItem}-{endItem} מתוך {totalCount} תזכורות
        </PaginationInfo>
        <PaginationButtons>
          <PaginationButton
            onClick={() => goToPage(pageParam - 1)}
            disabled={pageParam <= 1}
          >
            הקודם
          </PaginationButton>
          {pages.map(page => (
            <PaginationButton
              key={page}
              $active={page === pageParam}
              onClick={() => goToPage(page)}
            >
              {page}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => goToPage(pageParam + 1)}
            disabled={pageParam >= totalPages}
          >
            הבא
          </PaginationButton>
        </PaginationButtons>
      </PaginationContainer>
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <TitleIcon><Calendar size={26} /></TitleIcon>
          כל התזכורות
        </PageTitle>
      </PageHeader>

      <FiltersBar>
        <FilterGroup>
          <FilterLabel>חיפוש</FilterLabel>
          <SearchWrapper>
            <SearchIcon><Search size={16} /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="חיפוש בכותרת או בתיאור..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </SearchWrapper>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>סטטוס</FilterLabel>
          <Select
            value={statusParam}
            onChange={(e) => updateParam('status', e.target.value)}
          >
            <option value="all">הכל</option>
            <option value={ReminderStatus.Pending}>ממתין</option>
            <option value={ReminderStatus.Handled}>טופל</option>
            <option value={ReminderStatus.Postponed}>נדחה</option>
            <option value={ReminderStatus.Cancelled}>בוטל</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>מקושר ל</FilterLabel>
          <Select
            value={entityTypeParam}
            onChange={(e) => handleEntityTypeChange(e.target.value)}
          >
            <option value="">הכל</option>
            <option value={EntityType.Lead}>ליד</option>
            <option value={EntityType.Client}>לקוח</option>
            <option value={EntityType.Case}>תיק</option>
            <option value={EntityType.General}>כללי</option>
          </Select>
        </FilterGroup>

        {(entityTypeParam === String(EntityType.Lead) || entityTypeParam === String(EntityType.Client)) && (
          <FilterGroup>
            <FilterLabel>{entityTypeParam === String(EntityType.Lead) ? 'בחירת ליד' : 'בחירת לקוח'}</FilterLabel>
            {entityIdParam ? (
              <SelectedChip>
                {entityNameParam || `#${entityIdParam}`}
                <ChipClear onClick={handleEntityClear} title="נקה">
                  <X size={14} />
                </ChipClear>
              </SelectedChip>
            ) : (
              <EntityPicker
                kind={entityTypeParam === String(EntityType.Lead) ? 'lead' : 'client'}
                onSelect={handleEntitySelect}
              />
            )}
          </FilterGroup>
        )}

        <FilterGroup>
          <FilterLabel>סוג</FilterLabel>
          <Select
            value={typeParam}
            onChange={(e) => updateParam('type', e.target.value)}
          >
            <option value="">הכל</option>
            <option value={ReminderType.Call}>{getReminderTypeLabel(ReminderType.Call)}</option>
            <option value={ReminderType.FollowUp}>{getReminderTypeLabel(ReminderType.FollowUp)}</option>
            <option value={ReminderType.Document}>{getReminderTypeLabel(ReminderType.Document)}</option>
            <option value={ReminderType.Meeting}>{getReminderTypeLabel(ReminderType.Meeting)}</option>
            <option value={ReminderType.Payment}>{getReminderTypeLabel(ReminderType.Payment)}</option>
            <option value={ReminderType.Custom}>{getReminderTypeLabel(ReminderType.Custom)}</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>עדיפות</FilterLabel>
          <Select
            value={priorityParam}
            onChange={(e) => updateParam('priority', e.target.value)}
          >
            <option value="">הכל</option>
            <option value={ReminderPriority.High}>גבוהה</option>
            <option value={ReminderPriority.Medium}>בינונית</option>
            <option value={ReminderPriority.Low}>נמוכה</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>מתאריך</FilterLabel>
          <DateInput
            type="date"
            value={fromParam}
            onChange={(e) => updateParam('from', e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>עד תאריך</FilterLabel>
          <DateInput
            type="date"
            value={toParam}
            onChange={(e) => updateParam('to', e.target.value)}
          />
        </FilterGroup>
      </FiltersBar>

      {loading ? (
        <StateBox>טוען תזכורות...</StateBox>
      ) : error ? (
        <ErrorBox>{error}</ErrorBox>
      ) : reminders.length === 0 ? (
        <StateBox>לא נמצאו תזכורות</StateBox>
      ) : (
        <>
          <RemindersList>
            {reminders.map(reminder => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                onUpdate={loadReminders}
              />
            ))}
          </RemindersList>
          {renderPagination()}
        </>
      )}
    </PageContainer>
  );
};

export default RemindersPage;
